/**
 * The stack's compliance strip: one chip per LAYER, plus the roll-up that
 * answers "did the pavement pass?" without opening anything.
 *
 * A construction stack is N surface LEVELS giving N−1 LAYERS of material.
 * This strip is the dock's layer SELECTOR as well as its verdict summary —
 * spec §6.2 puts exactly ONE layer per run in the project tree, so the chip
 * strip is the only place the reader chooses which of the N−1 layers the
 * table, the chart and the cross-section are showing.
 *
 * ## `PENDING` is not a severity (ruling R13)
 *
 * `SEVERITY` in `VerdictChip.tsx` is imported, never redefined: a layer amber
 * in the strip and red in the chip is worse than no colour at all. But it is
 * also never EDITED. `VerdictChip.tsx:32` declares
 * `export type Verdict = keyof typeof SEVERITY`, so a fifth key there widens
 * `Verdict` to include `'PENDING'` across the whole shipped
 * surface-expectations feature — `SurfaceDifferencePanel`, `stationVolumes/
 * types.ts`, every per-bin verdict cell — none of which can ever receive a
 * PENDING and all of which would silently start accepting one.
 *
 * So the map is extended LOCALLY, and `LAYER_SEVERITY.PENDING` deliberately
 * does not borrow `INCONCLUSIVE`'s appearance: "not built yet" and
 * "measured, could not judge" are different facts about a pavement, and a
 * reader who cannot tell them apart cannot tell a programme problem from a
 * quality problem.
 */
import { SEVERITY, VerdictChip, type Criterion, type Verdict } from '@/components/VerdictChip';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * A LAYER can be in a state no single-surface comparison can reach: not built
 * yet. `PENDING` is that state — excluded from the roll-up rather than
 * counted as a pass (global constraint: absence is a state, never a zero).
 */
export type LayerVerdict = Verdict | 'PENDING';

export const LAYER_SEVERITY: Record<LayerVerdict, { glyph: string; cls: string }> = {
  ...SEVERITY,
  // A muted dot, not INCONCLUSIVE's grey question mark. Slate-400 at 10 %
  // sits visibly below the four real severities without reading as one.
  PENDING: { glyph: '·', cls: 'bg-slate-400/10 text-slate-500 dark:text-slate-400' },
};

export interface StackLayerChip {
  /** Zero-based layer index — `layers[i]` in the stack document. */
  i: number;
  /** `"<levelFrom> → <levelTo>"`, built by the worker. */
  name: string;
  verdict: LayerVerdict;
}

export interface LayerComplianceStripProps {
  layers: StackLayerChip[];
  /** Worst severity across the stack, `PENDING` when nothing is built yet. */
  rollUp: LayerVerdict;
  activeIndex: number;
  onSelect: (i: number) => void;
  /**
   * Criteria behind the roll-up, for the openable chip. Empty until the stack
   * document itself is loaded — the layer's metadata carries verdicts only
   * (the measurement blocks are megabytes and stay in the byproduct).
   */
  rollUpCriteria?: Criterion[];
}

/** Layers that carry a real verdict. PENDING ones are excluded from every
 *  count, exactly as `layer_stack.py::roll_up` excludes them. */
export const judgedLayers = (layers: StackLayerChip[]) => layers.filter((l) => l.verdict !== 'PENDING');

export const LayerComplianceStrip = ({
  layers,
  rollUp,
  activeIndex,
  onSelect,
  rollUpCriteria = [],
}: LayerComplianceStripProps) => {
  const { t } = useTranslation('project');
  const judged = judgedLayers(layers);
  const failed = judged.filter((l) => l.verdict === 'FAIL').length;
  const pending = layers.length - judged.length;

  return (
    <div
      data-testid='stack-rollup'
      data-roll-up={rollUp}
      className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
      <span className='font-semibold'>{t('layerStack.dock.complianceLabel')}</span>
      {/* The roll-up is the one chip that OPENS: a strip that says FAIL and
          nothing else starts an argument, the criteria list settles it. A
          PENDING roll-up has no criteria to show and no verdict to defend —
          nothing has been built — so it is a plain chip. */}
      {rollUp === 'PENDING' ? (
        <span
          data-testid='stack-rollup-verdict'
          className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
            LAYER_SEVERITY.PENDING.cls,
          )}>
          <span aria-hidden='true'>{LAYER_SEVERITY.PENDING.glyph}</span>
          {t('layerStack.dock.verdictPending')}
        </span>
      ) : (
        <span data-testid='stack-rollup-verdict'>
          <VerdictChip verdict={rollUp} criteria={rollUpCriteria} />
        </span>
      )}
      {/* The sentence, not just the colour: how many layers failed and how
          many are simply not built yet. A PENDING layer is never counted as a
          pass and never as a failure. */}
      <span data-testid='stack-rollup-counts' className='text-slate-500 dark:text-stone-400'>
        {t('layerStack.dock.rollUpCounts', { judged: judged.length, total: layers.length, failed, pending })}
      </span>
      <div role='tablist' aria-label={t('layerStack.dock.layerSelector')} className='flex min-w-0 flex-wrap gap-1'>
        {layers.map((l) => {
          const s = LAYER_SEVERITY[l.verdict] ?? LAYER_SEVERITY.INCONCLUSIVE;
          const active = l.i === activeIndex;
          return (
            <button
              key={l.i}
              type='button'
              role='tab'
              aria-selected={active}
              data-testid={`stack-chip-${l.i}`}
              data-verdict={l.verdict}
              title={t('layerStack.dock.chipTitle', {
                name: l.name,
                verdict: t(l.verdict === 'PENDING' ? 'layerStack.dock.verdictPending' : `expectation.verdict.${l.verdict}`),
              })}
              onClick={() => onSelect(l.i)}
              className={cn(
                'inline-flex max-w-56 items-center gap-1 rounded px-2 py-0.5 text-xs font-medium focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none',
                s.cls,
                active ? 'ring-2 ring-sky-500' : 'opacity-80 hover:opacity-100',
              )}>
              {/* Colour is never the only cue: every chip carries its glyph. */}
              <span aria-hidden='true'>{s.glyph}</span>
              <span className='truncate'>{l.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
